import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import FlowGraphNodeHandle from './FlowGraphNodeHandle';

class FlowGraphNode extends React.Component {
	constructor(props) {
		super(props);
		this.initConnect = this.initConnect.bind(this);
		this.startConnect = this.startConnect.bind(this);
		this.onTargetSelect = this.onTargetSelect.bind(this);
		this.onDetailMouseEnter = this.onDetailMouseEnter.bind(this);
		this.onDetailMouseLeave = this.onDetailMouseLeave.bind(this);
		this.handleSelectDetail = this.handleSelectDetail.bind(this);
		this.handleSelectOutput = this.handleSelectOutput.bind(this);
		this.outputEls = {};
		this.state = {
			connecting: props.connecting
		};
	}

	componentWillReceiveProps(props) {
		// Connecting is going in state as we want to
		// delay the connector but delay the edge line.
		this.state.connecting = props.connecting;
	}

	componentDidMount() {
		const { outputHandles } = this.props;
		outputHandles && outputHandles(this.getOutputPoints());
	}

	componentDidUpdate() {
		const { outputHandles } = this.props;
		outputHandles && outputHandles(this.getOutputPoints());
	}

	getOutputPoints() {
		return Object.keys(this.outputEls).filter(el => this.outputEls[el]).reduce(
			(col, cur) => {
				const el = this.outputEls[cur];
				col[cur] = {
					x: el.offsetLeft + (el.offsetWidth / 2),
					y: el.offsetTop + el.offsetHeight
				};
				return col;
			}, {}
		);
	}

	/*
	 * Render the icon or image for the node handler.
	 */
	renderIcon() {
		const { nodehandler } = this.props;
		let icon = 'icon-GG-code';
		if (nodehandler && nodehandler.icon) {
			icon = nodehandler.icon;
		}

		if (icon.match(/^data:image\/(?:bmp|jpeg|png|gif|tiff|svg\+xml);base64,.*/)) {
			return (
				<span className="node-icon" >
					<img src={icon} alt="" />
				</span>
			);
		} else {
			return (
				<span className={`node-icon gg ${icon}`} />
			);
		}
	}

	getMethodSpec() {
		const { nodehandler, node } = this.props;
		const method = node && node.method;

		if (!nodehandler || !nodehandler.methods || !nodehandler.methods.hasOwnProperty(method)) {
			return null;
		}
		return nodehandler.methods[method];
	}

	getOutputs() {
		// FIXME: this can return the same output twice (i.e. two 'next')
		const { node, invalidOutput } = this.props;
		// Get the flow-node method
		const methodSpec = this.getMethodSpec();
		if (methodSpec) {
			// Need to check for the output existing since the new validation
			// may not have come back yet after updating the flow
			const unknownOutputs = Object.keys(invalidOutput || {}).filter(id => (
				invalidOutput[id] === 'NOT_IN_SPEC' && node.outputs[id]
			));
			const outputs = [];
			for (const id in methodSpec.outputs) {
				outputs.push({
					id,
					name: methodSpec.outputs[id].name || id,
					invalid: invalidOutput && !!invalidOutput[id]
				});
			}
			for (const id of unknownOutputs) {
				outputs.push({
					id,
					name: node.outputs[id].metaName || id,
					invalid: true
				});
			}
			return outputs;
		} else {
			// If the method is missing, return the outputs from the flow definition
			// (last good state)
			return Object.keys(node.outputs).map(id => ({
				id,
				name: node.outputs[id].metaName
			}));
		}
	}

	renderNodeHandlerDetail() {
		const { node, invalid } = this.props;
		const icon = this.renderIcon();
		const name = node.name || node.id;
		const className = cn('detail', { invalid });
		return (
			<div
				className={className}
				onClick={this.handleSelectDetail}
				onMouseUp={this.onTargetSelect}
				onMouseEnter={this.onDetailMouseEnter}
				onMouseLeave={this.onDetailMouseLeave}
			>
				<FlowGraphNodeHandle />
				<div className="content">
					{icon}
					<div
						className="text node-name"
						title={name}
					>
						{name}
					</div>
					{invalid && <span className="node-status-icon gg icon-GG-warning" />}
				</div>
			</div>
		);
	}

	handleSelectDetail() {
		this.props.onSelect(this.props.id, { type: 'parameters' });
	}

	handleSelectOutput(output) {
		this.props.onSelect(this.props.id, { type: 'outputs', output });
	}

	renderNodeHandlerOutputs() {
		const { connecting } = this.state;
		const outputs = this.getOutputs();
		if (!outputs.length) {
			return;
		}

		return (
			<div className="outputs">
				{
					outputs.map(({ id: output, name, invalid }) => {
						const connectingOutput = connecting && connecting.source
							&& (connecting.output === output);
						return (
							<div
								id={output}
								key={output}
								ref={(r) => { this.outputEls[output] = r; }}
								className={cn('output', { source: connectingOutput, invalid })}
							>
								<div
									className={cn('name', { invalid })}
									onClick={() => this.handleSelectOutput(output)}
									onMouseDown={
										(e) => (e.button === 0) && this.initConnect(e, output)
									}
									onMouseLeave={this.startConnect}
								>
									{name}
								</div>
								<FlowGraphNodeHandle />
							</div>
						);
					})
				}
			</div>
		);
	}

	initConnect(e, output) {
		this.setState({
			connecting: {
				source: true,
				targetable: false,
				output
			}
		});
	}

	onDetailMouseEnter() {
		const { connecting } = this.state;

		if (connecting && connecting.targetable && !connecting.preview) {
			connecting.preview = true;
			this.setState(
				connecting,
				() => this.props.onTargetSelectable
					&& this.props.onTargetSelectable(this.props.id, true)
			);
		}
	}

	onDetailMouseLeave() {
		const { connecting } = this.state;

		if (connecting && connecting.targetable && connecting.preview) {
			connecting.preview = false;
			this.setState(
				connecting,
				() => this.props.onTargetSelectable
					&& this.props.onTargetSelectable(this.props.id, false)
			);
		}
	}

	onTargetSelect(e) {
		const { connecting } = this.state;
		if (connecting && connecting.targetable) {
			e && e.preventDefault();
			e && e.stopPropagation();
			this.props.onTargetSelect && this.props.onTargetSelect(this.props.id);
		}
	}

	startConnect() {
		const { connecting } = this.state;
		if (connecting && connecting.source) {
			this.props.onSelect && this.props.onSelect(this.props.id, {
				type: 'outputs',
				output: connecting.output
			});

			this.setState(
				{ connecting: null },
				() => this.props.onStartConnect
					&& this.props.onStartConnect(this.props.id, connecting.output)
			);
		}
	}

	render() {
		const { id, selected } = this.props;
		const { connecting } = this.state;

		const detail = this.renderNodeHandlerDetail();
		const outputs = this.renderNodeHandlerOutputs();

		// Not ideal but can't convince css to only grow based on num outputs and not on size of
		// text.
		const numOutputs = this.getOutputs().length;
		const outputWidth = 50;
		const outputSpacing = 6;
		const maxWidth = Math.max(
			135, (numOutputs * (outputWidth + outputSpacing)) - outputSpacing);

		let connectingClasses = '';
		if (connecting) {
			if (connecting.source) {
				connectingClasses = 'connecting source';
			} else if (connecting.targetable) {
				connectingClasses = cn('connecting target', { preview: connecting.preview });
			} else {
				connectingClasses = 'connecting nottargetable';
			}
		}

		const className = cn('node flow-graph-node', connectingClasses, {
			selected, 'no-outputs': !numOutputs
		});
		return (
			<div
				id={id}
				className={className}
				style={{
					maxWidth: `${maxWidth}px`
				}}
			>
				{detail}
				{outputs}
			</div>
		);
	}
}

FlowGraphNode.propTypes = {
	/**
	 * This ID of the node
	 */
	id: PropTypes.string.isRequired,
	/**
	 * The node to render.
	 */
	node: PropTypes.object.isRequired,
	/**
	 * The node handler.
	 */
	nodehandler: PropTypes.object.isRequired,
	/**
	 * Boolean to indicated if this node is selected.
	 */
	selected: PropTypes.bool,
	/**
	 * Render the node in connecting mode.
	 * source: true if this is the connection source.
	 * output: the output for the connection source (if this is the source).
	 * targetable: true if this node is a potential target for the connection.
	 */
	connecting: PropTypes.shape({
		source: PropTypes.bool,
		output: PropTypes.string,
		targetable: PropTypes.bool
	}),
	/**
	 * Callback when a node is selected.. Callback is passed this component id,
	 * and the selection type.
	 */
	onSelect: PropTypes.func.isRequired,
	/**
	 * Callback when a connection from this node is initiated. Callback is
	 * passed the node id and an object indicating the type of selection (parameters|outputs)
	 * and for output the output name.
	 */
	onStartConnect: PropTypes.func.isRequired,
	/**
	 * Callback when this node is the potential connecting target. Callback is passed this node id
	 * and a boolean - true if this node is toggled selectable, fales if it's toggled unselectable.
	 */
	onTargetSelectable: PropTypes.func.isRequired,
	/**
	 * Callback when this node is selected as the target. Callback is passed this node id.
	 */
	onTargetSelect: PropTypes.func.isRequired,
	/**
	 * Callback that recieves the bounding rects of the output nodes.
	 */
	outputHandles: PropTypes.func,
	/**
	 * Determines if the node is invalid or not. Invalid is denoted with an object
	 */
	invalid: PropTypes.bool,

	invalidOutput: PropTypes.object

};

export default FlowGraphNode;
